import { applyLegalMove, legalUciMoves } from "@chessla/shared";
import { boardTokens } from "@/shared/theme";
import type { ChessSquareStyles as CustomSquareStyles, ChessSquare as Square } from "@/shared/components/chessboard";

export interface LegalTarget {
  square: Square;
  kind: "quiet" | "capture";
  promotions: readonly ("q" | "r" | "b" | "n")[];
}

const promotionOrder = ["q", "r", "b", "n"] as const;

export const legalTargetsFrom = (fen: string, from: Square): readonly LegalTarget[] => {
  const targets = new Map<Square, { kind: LegalTarget["kind"]; promotions: Set<string> }>();
  for (const uci of legalUciMoves(fen)) {
    if (!uci.startsWith(from)) continue;
    const square = uci.slice(2, 4) as Square;
    const target = targets.get(square) ?? {
      kind: applyLegalMove(fen, uci)?.san.includes("x") ? "capture" : "quiet",
      promotions: new Set<string>(),
    };
    if (uci.length === 5) target.promotions.add(uci[4]);
    targets.set(square, target);
  }
  return [...targets].map(([square, target]) => ({
    square,
    kind: target.kind,
    promotions: promotionOrder.filter((promotion) => target.promotions.has(promotion)),
  }));
};

export const temporarySquareStyles = (
  targets: readonly LegalTarget[],
  lastReplyUci?: string,
  selectedSquare?: Square | null,
): CustomSquareStyles => {
  const styles: CustomSquareStyles = {};
  if (lastReplyUci) {
    const tint = { backgroundColor: boardTokens.interaction.reply };
    styles[lastReplyUci.slice(0, 2) as Square] = tint;
    styles[lastReplyUci.slice(2, 4) as Square] = tint;
  }
  if (selectedSquare) styles[selectedSquare] = { backgroundColor: boardTokens.interaction.selected };
  for (const target of targets) {
    styles[target.square] = {
      ...styles[target.square],
      backgroundImage:
        target.kind === "capture" ? boardTokens.interaction.captureTarget : boardTokens.interaction.quietTarget,
    };
  }
  return styles;
};
